import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  AlertCircle,
  ChevronDown,
  Disc,
  Download,
  FileArchive,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Imaging } from "@/features/imagings/schemas/imagings";
import {
  createImagingDownloadJob,
  pollImagingDownloadJobWithProgress,
} from "@/features/imagings/services/imagingService";
import { formatFileSize } from "@/shared/utils/imaging";

interface DownloadStudyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  imaging: Imaging;
}

const STORAGE_KEY = "download_job_active";

export function DownloadStudyDialog({
  isOpen,
  onClose,
  imaging,
}: DownloadStudyDialogProps): React.JSX.Element {
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const [isDownloadingISO, setIsDownloadingISO] = useState(false);
  const [isDownloadingZIP, setIsDownloadingZIP] = useState(false);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobMessage, setJobMessage] = useState("");
  const [jobStatus, setJobStatus] = useState<
    "idle" | "processing" | "completed" | "failed"
  >("idle");
  const activeJobRef = useRef<{
    jobId: string;
    uid: string;
    ext: string;
  } | null>(null);

  // Poll an existing job (used for resume after page reload)
  const pollExistingJob = useCallback(
    async (jobId: string, _uid: string, _ext: string) => {
      setJobStatus("processing");
      setJobProgress(0);
      setJobMessage("Reanudando descarga...");

      try {
        const url = await pollImagingDownloadJobWithProgress(jobId, (jobData) => {
          setJobProgress(jobData.progress || 0);
          setJobMessage(jobData.message || "Procesando...");
        });
        setJobStatus("completed");
        setJobProgress(100);
        setJobMessage("¡Descarga lista!");
        window.open(url, "_blank");
        sessionStorage.removeItem(STORAGE_KEY);
        activeJobRef.current = null;
      } catch (error: any) {
        console.error("Error reanudando descarga:", error);
        setJobStatus("failed");
        setJobMessage(error?.message || "Error durante la descarga");
        sessionStorage.removeItem(STORAGE_KEY);
        activeJobRef.current = null;
      }
    },
    []
  );

  // Resume active job on mount (page reload recovery)
  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;

    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const parsed = JSON.parse(stored);
      // Only resume if it's for the same study
      if (parsed.uid === imaging.iuid) {
        activeJobRef.current = {
          jobId: parsed.jobId,
          uid: parsed.uid,
          ext: parsed.ext,
        };
        pollExistingJob(parsed.jobId, parsed.uid, parsed.ext);
      } else {
        // Stale job for a different study — clear it
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [imaging.iuid, pollExistingJob]);

  const handleDownload = async (format: "iso" | "zip") => {
    try {
      if (!imaging.iuid) {
        toast.error("El estudio no posee un identificador válido (IUID)");
        return;
      }

      if (format === "iso") setIsDownloadingISO(true);
      if (format === "zip") setIsDownloadingZIP(true);

      setJobStatus("processing");
      setJobProgress(0);
      setJobMessage("Iniciando descarga...");

      const { job_id, status, url } = await createImagingDownloadJob({
        uid: imaging.iuid,
        ext: format,
      });

      // If file already existed, URL is returned immediately
      if (status === "completed" && url) {
        setJobStatus("completed");
        setJobProgress(100);
        setJobMessage("¡Descarga lista!");
        window.open(url, "_blank");
        return;
      }

      // Save job info to sessionStorage for reload recovery
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ jobId: job_id, uid: imaging.iuid, ext: format })
      );
      activeJobRef.current = { jobId: job_id, uid: imaging.iuid, ext: format };

      const downloadUrl = await pollImagingDownloadJobWithProgress(
        job_id,
        (jobData) => {
          setJobProgress(jobData.progress || 0);
          setJobMessage(jobData.message || "Procesando...");
        }
      );

      setJobStatus("completed");
      setJobProgress(100);
      setJobMessage("¡Descarga lista!");
      window.open(downloadUrl, "_blank");
      sessionStorage.removeItem(STORAGE_KEY);
      activeJobRef.current = null;
    } catch (error: any) {
      console.error("Error downloading study:", error);
      setJobStatus("failed");
      setJobMessage(error.message || "Error durante la descarga");
      sessionStorage.removeItem(STORAGE_KEY);
      activeJobRef.current = null;
    } finally {
      setIsDownloadingISO(false);
      setIsDownloadingZIP(false);
    }
  };

  const resetDownload = () => {
    setJobStatus("idle");
    setJobProgress(0);
    setJobMessage("");
    sessionStorage.removeItem(STORAGE_KEY);
    activeJobRef.current = null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden bg-slate-50 dark:bg-slate-950">
        <DialogHeader className="p-6 pb-4 bg-white dark:bg-slate-900 border-b relative">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
              <Download className="h-6 w-6" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold m-0">
                Descargar Estudio
              </DialogTitle>
              <DialogDescription className="text-sm mt-1 truncate max-w-[320px]">
                {imaging.modality} -{" "}
                {imaging.patient_name.replaceAll("^", " ").trim()}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 space-y-6 pt-5 bg-white dark:bg-slate-900">
          <div className="space-y-3">
            <h3 className="font-semibold text-[15px] text-foreground">
              Información del Estudio
            </h3>
            <div className="rounded-xl border bg-slate-50/50 p-4 space-y-3 dark:bg-slate-900/50 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Paciente:</span>
                <span className="text-sm font-medium">
                  {imaging.patient_name}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Modalidad:</span>
                <span className="text-sm font-medium">{imaging.modality}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  N° Acceso:
                </span>
                <span className="text-sm font-medium">
                  {imaging.accession_number}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Tamaño:</span>
                <span className="text-sm font-medium">
                  {formatFileSize(imaging.size)}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-[15px] text-foreground">
              Formato de Descarga
            </h3>

            <div className="grid gap-3">
              <button
                onClick={() => handleDownload("iso")}
                disabled={
                  isDownloadingISO || isDownloadingZIP || jobStatus === "processing"
                }
                className="w-full flex items-center justify-between p-4 rounded-xl border border-border/50 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800 transition-colors shadow-sm text-left disabled:opacity-50"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                    <Disc className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Formato ISO</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Ideal para grabar en CD/DVD
                    </p>
                  </div>
                </div>
                <div className="h-10 w-10 rounded-full border border-blue-200 dark:border-blue-800 flex items-center justify-center text-blue-600 dark:text-blue-400 shrink-0">
                  {isDownloadingISO ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </div>
              </button>

              <button
                onClick={() => handleDownload("zip")}
                disabled={
                  isDownloadingISO || isDownloadingZIP || jobStatus === "processing"
                }
                className="w-full flex items-center justify-between p-4 rounded-xl border border-border/50 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800 transition-colors shadow-sm text-left disabled:opacity-50"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400 shrink-0">
                    <FileArchive className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Formato ZIP</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Comprimido para almacenamiento
                    </p>
                  </div>
                </div>
                <div className="h-10 w-10 rounded-full border border-orange-200 dark:border-orange-800 flex items-center justify-center text-orange-600 dark:text-orange-400 shrink-0">
                  {isDownloadingZIP ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </div>
              </button>
            </div>

            {/* Progress indicator */}
            {jobStatus === "processing" && (
              <div className="mt-4 rounded-xl bg-slate-50 dark:bg-slate-800 p-4">
                <div className="mb-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                  <div
                    className="h-full rounded-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${jobProgress}%` }}
                  />
                </div>
                <p className="text-center text-sm text-muted-foreground">
                  {jobProgress}% — {jobMessage}
                </p>
              </div>
            )}

            {/* Error state */}
            {jobStatus === "failed" && (
              <div className="mt-4 flex items-center gap-3 rounded-xl bg-red-50 dark:bg-red-950/20 p-4">
                <AlertCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                <p className="flex-1 text-sm text-red-600 dark:text-red-400">
                  {jobMessage}
                </p>
                <button
                  className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
                  onClick={resetDownload}
                >
                  Reintentar
                </button>
              </div>
            )}
          </div>

          <div className="rounded-xl border bg-blue-50/50 dark:bg-blue-950/20 overflow-hidden">
            <button
              onClick={() => setIsInstructionsOpen(!isInstructionsOpen)}
              className="w-full flex items-center justify-between p-4 hover:bg-blue-50/80 dark:hover:bg-blue-900/30 transition-colors"
            >
              <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium text-sm">
                  Instrucciones para CD/DVD
                </span>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-blue-700 dark:text-blue-400 transition-transform ${isInstructionsOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isInstructionsOpen && (
              <div className="p-4 pt-0 border-t border-blue-100 dark:border-blue-900">
                <ol className="space-y-3 mt-3">
                  {[
                    "Descarga el estudio en formato ISO",
                    "Inserta un CD o DVD en blanco en tu unidad óptica",
                    'Click derecho en el archivo ISO y selecciona "Grabar imagen de disco"',
                    'Activa "Verificar disco después de grabar" para asegurar integridad',
                    'Haz click en "Grabar" y espera a que termine el proceso',
                  ].map((step, index) => (
                    <li
                      key={index}
                      className="flex gap-3 text-sm text-blue-900 dark:text-blue-200"
                    >
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[11px] font-bold text-white mt-0.5">
                        {index + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 pt-0 flex justify-center bg-white dark:bg-slate-900">
          <Button
            onClick={onClose}
            className="w-32 bg-blue-700 hover:bg-blue-800 text-white font-medium shadow-sm transition-all"
          >
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
